/**
 * The SystemConfig entity as the admin API serves it: the category enum, one
 * stored row (`SystemConfigSchema`) and one history snapshot. The request and
 * response shapes built on them are `system-config-requests.schema.ts` and
 * `../zsystem-config.ts`.
 */

import { z } from "zod";

import { CronConfigSchema } from "./cron-config.schema";

// ═══════════════════════════════════════════════════════════════
// ENUMS & CONSTANTS
// ═══════════════════════════════════════════════════════════════

/**
 * Config Category
 *
 * Type of operational configuration.
 * Validated in application layer (not DB CHECK constraint) for extensibility.
 */
export const ConfigCategory = {
  CRON: "cron",
  RATE_LIMIT: "rate_limit",
  CACHE: "cache",
  CIRCUIT_BREAKER: "circuit_breaker",
  RETRY_POLICY: "retry_policy",
  ECHO: "echo",
  DELETION: "deletion",
  CONVERSATION: "conversation",
  VIBE: "vibe",
  PUBLIC_API: "public_api",
  TWILIO: "twilio",
  AGENT_AUTH: "agent_auth",
  SANDBOX: "sandbox",
  DEPLOYMENTS: "deployments",
  AGENT_TOOLS: "agent-tools",
  IDENTITY: "identity"
} as const;

export type ConfigCategory = (typeof ConfigCategory)[keyof typeof ConfigCategory];

export const ConfigCategorySchema = z.enum([
  "cron",
  "rate_limit",
  "cache",
  "circuit_breaker",
  "retry_policy",
  "echo",
  "deletion",
  "conversation",
  "vibe",
  "public_api",
  "twilio",
  "agent_auth",
  "sandbox",
  "deployments",
  "agent-tools",
  "identity"
]);

// ═══════════════════════════════════════════════════════════════
// ENTITY SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * What the admin API serves of a key's code default, for every category. See
 * `SystemConfigSchema.codeDefault`.
 */
export const SystemConfigCodeDefaultSchema = z.object({
  /** The code's description of the key; `null` where its code default carries none. */
  description: z.string().nullable()
});

export type SystemConfigCodeDefault = z.infer<typeof SystemConfigCodeDefaultSchema>;

/**
 * System Config Entity Schema
 *
 * Represents a single configuration entry in the database.
 */
export const SystemConfigSchema = z.object({
  id: z.string().uuid(),
  category: ConfigCategorySchema,
  key: z.string().min(1).max(255),
  config: z.record(z.string(), z.unknown()),
  version: z.number().int().min(1),
  description: z.string().nullable(),
  enabled: z.boolean(),
  deprecated: z.boolean().optional(), // Config exists in DB but not in code defaults
  /**
   * The cron's CODE default — what the code ships for this key, as
   * `tryGetCronDefault(key)` returns it — served beside the row, never in place
   * of it. Present only on rows of category `cron`; absent for every other
   * category.
   *
   * It is the one reliable answer to "which fields does this cron declare"
   * (`batchSize` is writable exactly where the default declares it) and "what
   * are its code-owned values" (`cronKey`, `title`, `description`): the row's
   * own `config` can carry a stale `batchSize` or `title` the write path would
   * refuse to keep.
   *
   * `null` means the key has no code default — nothing registers it, so nothing
   * reads the row. That is the same fact `deprecated: true` states.
   */
  cronDefault: CronConfigSchema.nullable().optional(),
  /**
   * What the code ships for this row's key, served beside the row on EVERY
   * category: the code's own description, which the admin panel shows as the
   * row's description. The row's `description` column is the admin's note and
   * nothing else (`CONFIG_DESCRIPTION_COLUMN_OWNERSHIP`), shown apart from it.
   *
   * `null` means the key has no code default at all; a `description` of `null`
   * means the code default carries none.
   */
  codeDefault: SystemConfigCodeDefaultSchema.nullable(),
  /**
   * Whether the stored `config` parses against its category's schema — the same
   * validation every write goes through. A row written any other way (a data
   * migration, a hand edit, a key the code no longer registers) can fail it, and
   * is then served with `valid: false` rather than as if it were a setting the
   * running system accepts, and rather than failing the whole list.
   */
  valid: z.boolean(),
  /** Why `valid` is false, in the validator's own words; `null` on a valid row. */
  parseError: z.string().nullable(),
  /**
   * Whether this row is in effect, as the running system decides it — the value
   * the admin list shows and the `enabled` query filters on.
   *
   *   - A cron runs unless its stored `config.enabled` is `false`, exactly as the
   *     scheduler reads it; a cron with no code default is never registered, so
   *     it is never in effect.
   *   - Every other category has no on/off: its readers take `config` whatever
   *     the `enabled` column says (see `CONFIG_CATEGORIES_WITH_ENABLED_SETTING`),
   *     so its row is always in effect.
   */
  effectiveEnabled: z.boolean(),
  createdBy: z.string(),
  updatedBy: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

export type SystemConfig = z.infer<typeof SystemConfigSchema>;

/**
 * System Config History Entry Schema
 *
 * Represents a historical snapshot of a config change.
 */
export const SystemConfigHistorySchema = z.object({
  id: z.string().uuid(),
  configId: z.string().uuid().nullable(),
  configKey: z.string(),
  configCategory: ConfigCategorySchema,
  config: z.record(z.string(), z.unknown()),
  version: z.number().int().min(1),
  changedBy: z.string(),
  changedAt: z.coerce.date(),
  changeReason: z.string().nullable()
});

export type SystemConfigHistory = z.infer<typeof SystemConfigHistorySchema>;
